import { LevelPool, useUser } from '@trustpad/launchpad';
import { useStakingV2 } from '@trustpad/launchpad/dist/staking/hooks/useStakingV2';
import * as React from 'react';
import { useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';

import { BscContractLink, DividerLine } from '~common/UI';
import { ClaimBlock } from '~features/tiers/components/ClaimBlock';
import { StakeBlock } from '~features/tiers/components/StakeBlock';
import { UnstakeBlock } from '~features/tiers/components/UnstakeBlock';
import { UpgradeBlock } from '~features/tiers/components/UpgradeBlock';
import { Alert } from '~features/ui';
import { fNum } from '~utils';
import { StakingConfigOverview } from './StakingConfigOverview';
import { UserDepositStatusAndProgress } from './UserDepositStatusAndProgress';

export function StakingSection({
  stakingConfig,
  refresh,
}: {
  stakingConfig: LevelPool;
  refresh: VoidFunction;
}) {
  const { refresh: refreshUser, account } = useUser();
  const {
    stakingContract,
    state,
    user,
    refresh: refreshStaking,
  } = useStakingV2();

  const refreshAll = useCallback(() => {
    refresh();
    refreshStaking();
    refreshUser();
  }, [refresh, refreshUser, refreshStaking]);

  const tpadPerDay = user.amount.muln(stakingConfig.apy).divn(100).divn(365);

  // if (state.halted) {
  //   return (
  //     <div className="text-xl">
  //       Staking in this pool is disabled while the migration to TPAD 2.0 is
  //       ongoing.
  //     </div>
  //   );
  // }

  return (
    <>
      <div className="relative bg-purple-800 bg-opacity-20 -mx-5 p-5 border-t border-b border-gray-700 border-opacity-50 space-y-6">
        {tpadPerDay.gtn(0) ? (
          <div className="text-center flex-shrink-0">
            <span className="brand-text text-5xl">
              {fNum(tpadPerDay, 3)} <span className="text-xl">TPAD</span>
            </span>
            <div className="text-muted leading-none">Reward per day</div>
          </div>
        ) : null}

        {state.rewardToken ? (
          <StakingConfigOverview state={state} stakingConfig={stakingConfig} />
        ) : (
          <Skeleton count={4} width="100%" />
        )}

        <UserDepositStatusAndProgress
          user={user}
          lockPeriod={state.lockPeriod}
        />

        {stakingConfig.deprecated && (
          <Alert type="warning">
            This pool is deprecated and does not contribute to your level
            anymore. Please upgrade to the higher one or withdraw.
          </Alert>
        )}

        <ClaimBlock
          pendingRewards={user.pendingRewards}
          maturityAt={
            state.waitForRewardMaturity
              ? user.lastClaimedAt +
                (state.rewardMaturityDuration || state.lockPeriod)
              : 0
          }
          stakingContract={stakingContract}
          refresh={refreshAll}
          halted={state.halted}
        />

        <DividerLine className="pb-1" />

        <div className="space-y-6">
          {!stakingConfig.deprecated && (
            <StakeBlock
              // halted={state.halted}
              halted={false}
              refreshAll={refreshAll}
              stakingContract={stakingContract}
            />
          )}

          {user.amount.gtn(0) && (
            <UnstakeBlock
              userStats={user}
              refreshAll={refreshAll}
              stakingContract={stakingContract}
              fee={state.fees.withdrawFee}
              allowEarlyWithdrawal={state.allowEarlyWithdrawal}
              halted={state.halted}
            />
          )}

          <UpgradeBlock
            userStats={user}
            refreshAll={refreshAll}
            stakingContract={stakingContract}
            currentStaking={state}
          />
        </div>

        {process.env.NODE_ENV === 'development' && (
          <div className="absolute right-3 bottom-3">
            <BscContractLink address={stakingContract?.options.address} />
          </div>
        )}
      </div>
    </>
  );
}
